import * as React from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { getPortalId } from "./PopoverPortal";

interface OpenCloseParams {
  delay?: number;
}

function isOpenCloseParams(args: OpenCloseParams | React.MouseEvent<HTMLElement>): args is OpenCloseParams {
  return !("currentTarget" in args);
}

interface RenderProps {
  left: number;
  top: number;
  onClose(args?: OpenCloseParams | React.MouseEvent<HTMLElement>): void;
  onOpen(args?: OpenCloseParams | React.MouseEvent<HTMLElement>): void;
}

interface ChildrenProps {
  isOpen: boolean;
  onOpen(args?: OpenCloseParams | React.MouseEvent<HTMLElement>): void;
  onClose(args?: OpenCloseParams | React.MouseEvent<HTMLElement>): void;
  setRef(ref: HTMLElement | null): void;
}

interface Props {
  portalName?: string;
  render(props: RenderProps): React.ReactNode;
  children(props: ChildrenProps): React.ReactNode;
}

interface State {
  isOpen: boolean;
  left: number;
  top: number;
}

class PopoverContainer extends React.Component<Props, State> {
  public static contextTypes = {
    popoverPortalName: PropTypes.string,
  };

  public state = {
    isOpen: false,
    left: 0,
    top: 0,
  };

  private portal?: HTMLElement;
  private innerRef: HTMLElement | null = null;
  private targetRef: HTMLElement | null = null;
  private closeTimer?: NodeJS.Timer;

  public constructor(props: Props) {
    super(props);
  }

  public componentDidMount() {
    if (typeof window !== "undefined") {
      const portalName = this.getPortalName(this.props);
      const portal = document.getElementById(getPortalId(portalName));
      if (!portal) {
        throw new Error(`PopoverPortal element is not found. Please mount <PopoverPortal name=\"${portalName}\" />`);
      }
      this.portal = portal;
      if (module.hot) {
        setTimeout(() => {
          this.forceUpdate();
        }, 0);
      }
      window.addEventListener("touchend", this.handleClickOutsidePopover);
    }
  }

  public componentWillUnmount() {
    this.portal = undefined;
    this.innerRef = null;
    this.targetRef = null;
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = undefined;
    }
    window.removeEventListener("touchend", this.handleClickOutsidePopover);
  }

  public getPortalName(props: Props) {
    return props.portalName || this.context.popoverPortalName || "default";
  }

  public onOpen = (args?: OpenCloseParams | React.MouseEvent<HTMLElement>) => {
    let delay = 0;
    if (args && isOpenCloseParams(args)) {
      delay = args.delay || 0;
    }
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = undefined;
    }
    if (delay <= 0) {
      this.setState({
        isOpen: true,
        ...this.getPopoverPosition(),
      });
    } else {
      setTimeout(() => this.setState({ isOpen: true, ...this.getPopoverPosition() }), delay);
    }
  };

  public onClose = (args?: OpenCloseParams | React.MouseEvent<HTMLElement>) => {
    let delay = 0;
    if (args && isOpenCloseParams(args)) {
      delay = args.delay || 0;
    }
    if (delay <= 0) {
      this.setState({
        isOpen: false,
      });
    } else {
      this.closeTimer = setTimeout(() => this.setState({ isOpen: false }), delay);
    }
  };

  public onClickOverlay = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (this.innerRef && this.innerRef.contains(event.target as HTMLElement)) {
      // Do nothing
    } else {
      this.onClose();
    }
  };

  public handleClickOutsidePopover = () => {
    const { isOpen } = this.state;
    if (isOpen) {
      this.onClose();
    }
  };

  public getPopoverPosition() {
    let left = 0;
    let top = 0;
    if (this.portal && this.targetRef) {
      const targetRect = this.targetRef.getBoundingClientRect();
      const portalRect = this.portal.getBoundingClientRect();
      left = targetRect.left - portalRect.left;
      top = targetRect.top - portalRect.top;
    }
    return { left, top };
  }

  public render() {
    const { children } = this.props;
    const { isOpen, left, top } = this.state;
    return (
      <>
        {children({
          isOpen,
          onOpen: this.onOpen,
          onClose: this.onClose,
          setRef: ref => (this.targetRef = ref),
        })}
        {this.portal &&
          createPortal(
            isOpen ? (
              <>
                <div onClick={this.onClickOverlay}>
                  <div ref={ref => (this.innerRef = ref)}>
                    {this.props.render({
                      onClose: this.onClose,
                      onOpen: this.onOpen,
                      left,
                      top,
                    })}
                  </div>
                </div>
              </>
            ) : null,
            this.portal
          )}
      </>
    );
  }
}

export default PopoverContainer;



// WEBPACK FOOTER //
// ./frontend/src/common/components/popover/PopoverContainer.tsx